/** Кривая пути уровней (макет «Экспедиция»): кубические сегменты Безье, точки и пунктир. */

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Сегмент: две контрольные точки и конец; начало — конец предыдущего. */
export interface Cubic {
  readonly c1: Point;
  readonly c2: Point;
  readonly to: Point;
}

/**
 * Сегменты через узлы: первая и последняя контрольные точки — как у SVG-команды `S`
 * (первая точка сегмента — отражение предыдущей), середина сегмента — по горизонтали.
 */
export function smoothPath(nodes: readonly Point[]): { start: Point; segments: Cubic[] } {
  const start = nodes[0] ?? { x: 0, y: 0 };
  const segments: Cubic[] = [];
  let prev = start;
  let lastC2: Point | null = null;
  for (const to of nodes.slice(1)) {
    const half = (to.x - prev.x) / 2;
    const c1: Point = lastC2
      ? { x: 2 * prev.x - lastC2.x, y: 2 * prev.y - lastC2.y }
      : { x: prev.x + half, y: prev.y };
    const c2: Point = { x: to.x - half, y: to.y };
    segments.push({ c1, c2, to });
    lastC2 = c2;
    prev = to;
  }
  return { start, segments };
}

function cubicAt(p0: Point, s: Cubic, t: number): Point {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * s.c1.x + c * s.c2.x + d * s.to.x,
    y: a * p0.y + b * s.c1.y + c * s.c2.y + d * s.to.y,
  };
}

/** Ломаная, приближающая кривую: `steps` точек на сегмент. */
export function samplePath(
  path: { start: Point; segments: readonly Cubic[] },
  steps = 24,
): Point[] {
  const points: Point[] = [path.start];
  let p0 = path.start;
  for (const segment of path.segments) {
    for (let i = 1; i <= steps; i++) points.push(cubicAt(p0, segment, i / steps));
    p0 = segment.to;
  }
  return points;
}

/** Отрезки пунктира вдоль ломаной: штрих `dash`, пробел `gap`, сдвиг узора `offset`. */
export function dashes(
  points: readonly Point[],
  dash: number,
  gap: number,
  offset: number,
): [Point, Point][] {
  const period = dash + gap;
  const result: [Point, Point][] = [];
  // Позиция по длине, где начинается первый штрих (узор сдвигается на offset).
  const phase = (((-offset % period) + period) % period) - period;
  let travelled = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const at = (d: number): Point => ({
      x: a.x + ((b.x - a.x) * (d - travelled)) / length,
      y: a.y + ((b.y - a.y) * (d - travelled)) / length,
    });
    // Все штрихи, пересекающие этот кусок ломаной.
    let dashStart = phase + Math.floor((travelled - phase) / period) * period;
    while (dashStart < travelled + length) {
      const from = Math.max(dashStart, travelled);
      const to = Math.min(dashStart + dash, travelled + length);
      if (to > from) result.push([at(from), at(to)]);
      dashStart += period;
    }
    travelled += length;
  }
  return result;
}
